/**
 * JP-LCC — John Person's "Last Conditional Change" lines, a support and a
 * resistance. SweeterTrade's rule (2026-11-02), solved on their exported
 * `lccSupport` / `lccResistance` columns: META daily, NVDA daily and NVDA weekly
 * agree on every bar after the warm-up but six, each a rounding tie in their
 * four-decimal prices.
 *
 *   close > high[1] -> support    = min(low, low[1])   last change = +1
 *   close < low[1]  -> resistance = high[1]            last change = -1
 *
 * A change SETS its line, so support can move down as well as up, and
 * resistance is the prior bar's high alone (not this bar's). A line keeps its
 * value until the next change of its own kind. Support is SHOWN while the last
 * change was +1 or the close is above it; resistance while the last change was
 * -1 or the close is below it (both strict). So both lines can show at once,
 * and a line that is not shown comes back at the value it kept.
 *
 * Everything on bar i is read from bars i and i-1, on the bar itself: no display
 * lag, and nothing drawn at a past bar changes when later bars arrive.
 *
 * Until 2026-11-02 this was a rule fitted to a TradeStation `jp-lcc` export
 * (the lowest low since the line went off, raised only, shown a bar late).
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import { resampleOHLC, htIndexPerDay, type HtFreq } from "./weekly";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "../types";

export type LccParts = {
  /** Support line value per bar, or null where it is not shown. */
  up: (number | null)[];
  /** Resistance line value per bar, or null where it is not shown. */
  down: (number | null)[];
  /** True on the bar where the support line appears after a bar without it — for
   *  an optional ▲ marker. */
  upArm: boolean[];
  /** True on the bar where the resistance line appears after a bar without it —
   *  for an optional ▼ marker. */
  downArm: boolean[];
};

export function computeLcc(data: OHLCV): LccParts {
  const { highs, lows, closes } = data;
  const n = closes.length;
  const up: (number | null)[] = new Array(n).fill(null);
  const down: (number | null)[] = new Array(n).fill(null);
  const upArm = new Array<boolean>(n).fill(false);
  const downArm = new Array<boolean>(n).fill(false);
  if (!highs || !lows || n === 0) return { up, down, upArm, downArm };

  let sup: number | null = null; // the support's value once a change has set it
  let res: number | null = null; // the resistance's value once a change has set it
  let last = 0; // +1 after a close above the prior high, -1 after one below the prior low

  for (let t = 1; t < n; t++) {
    const c = closes[t]!;
    if (c > highs[t - 1]!) { sup = Math.min(lows[t]!, lows[t - 1]!); last = 1; }
    if (c < lows[t - 1]!) { res = highs[t - 1]!; last = -1; }
    if (sup != null && (last === 1 || c > sup)) up[t] = sup;
    if (res != null && (last === -1 || c < res)) down[t] = res;
    upArm[t] = up[t] != null && up[t - 1] == null;
    downArm[t] = down[t] != null && down[t - 1] == null;
  }
  return { up, down, upArm, downArm };
}

// ── LCC Trend (Bull / Bear / Neutral) ────────────────────────────────────
// Active-line rule on the lines as shown: Bull while ONLY the support line
// shows, Bear while ONLY the resistance line shows, Neutral when both
// (range-bound) or neither does. The lines carry no display lag, so a close
// through a line moves the Trend on that bar.
export function lccTrendSeries(data: OHLCV): number[] {
  const { up, down } = computeLcc(data);
  return up.map((u, i) => {
    const hasUp = u != null, hasDn = down[i] != null;
    return hasUp && !hasDn ? 1 : hasDn && !hasUp ? -1 : 0;
  });
}

/** Latest LCC trend on the in-progress bar: +1 Bull / -1 Bear / 0 Neutral. */
export function lccTrendLatest(data: OHLCV): number | null {
  if (!data.closes || data.closes.length === 0) return null;
  const s = lccTrendSeries(data);
  return s[s.length - 1] ?? 0;
}

const tfFreq = (tf: number | undefined): HtFreq | "day" =>
  tf === 2 ? "month" : tf === 1 ? "week" : "day";

/** LCC trend on the chosen timeframe, projected onto daily bars by period. */
function lccTfSeries(data: OHLCV, freq: HtFreq | "day"): (number | null)[] {
  if (freq === "day") return lccTrendSeries(data);
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  const st = lccTrendSeries({ opens: h.o, highs: h.h, lows: h.l, closes: h.c } as OHLCV);
  const idx = htIndexPerDay(times, h.t);
  return times.map((_, i) => { const k = idx[i]!; return k >= 0 && k < st.length ? st[k]! : null; });
}

const LCC_LABELS = { pos: "Bull", neg: "Bear", zero: "Neutral" } as const;

/** JP LCC Trend — which of the support and resistance lines shows (above) on a
 *  Daily/Weekly/Monthly Timeframe select. Instant off the baked lcc_d/w/m payload fields
 *  (panelKey = Daily; W/M via TF_PRECOMPUTED). */
export const LCC_TREND: SharedIndicatorDef = {
  id: "mpp-lcc-trend",
  label: "Afiat - Trailing Pivot Stops",
  group: familyGroup("lcc"), // → "Pivot Studies"
  kind: "state",
  format: "state",
  stateLabels: LCC_LABELS,
  panelKey: "lcc_d", // Daily; Weekly/Monthly via TF_PRECOMPUTED (lcc_w / lcc_m)
  params: [TF_DWM],
  computeSeries: (d: OHLCV, p: IndicatorParams) => lccTfSeries(d, tfFreq(p[TF_PARAM])),
};

export const LCC_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [LCC_TREND];
